import { RotateCcw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getUser, patchUser } from "../services/api";
import type { EditableField, EditableUserProfile, ManagerSummary, ProfilePatch } from "../types/peopleAdmin";
import AuditPanel from "./AuditPanel";
import ManagerPicker from "./ManagerPicker";
import PhotoUploader from "./PhotoUploader";

interface EmployeeProfileEditorProps {
  userId: string;
  division: string;
  onSaved: () => void;
}

interface ProfileForm {
  displayName: string;
  givenName: string;
  surname: string;
  jobTitle: string;
  department: string;
  officeLocation: string;
  mobilePhone: string;
  businessPhones: string;
  companyName: string;
  employeeId: string;
  manager: ManagerSummary | null;
}

const textFields: Array<{ key: keyof Omit<ProfileForm, "businessPhones" | "manager">; label: string; required?: boolean }> = [
  { key: "displayName", label: "Display name", required: true },
  { key: "givenName", label: "Given name" },
  { key: "surname", label: "Surname" },
  { key: "jobTitle", label: "Job title" },
  { key: "department", label: "Department" },
  { key: "officeLocation", label: "Office location" },
  { key: "mobilePhone", label: "Mobile phone" },
  { key: "companyName", label: "Company name" },
  { key: "employeeId", label: "Employee ID" }
];

export default function EmployeeProfileEditor({ userId, division, onSaved }: EmployeeProfileEditorProps) {
  const [profile, setProfile] = useState<EditableUserProfile | null>(null);
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [initialForm, setInitialForm] = useState<ProfileForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      setMessage(null);

      try {
        const loaded = await getUser(userId);
        const loadedForm = toForm(loaded);
        if (mounted) {
          setProfile(loaded);
          setForm(loadedForm);
          setInitialForm(loadedForm);
        }
      } catch (loadError) {
        if (mounted) {
          setError(loadError instanceof Error ? loadError.message : "Could not load profile.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const dirty = useMemo(() => {
    if (!form || !initialForm) {
      return false;
    }

    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const allowedFields = useMemo(() => new Set<EditableField>(profile?.allowedFields || []), [profile]);

  async function save() {
    if (!profile || !form || !initialForm) {
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const validationError = validateForm(form, division);
      if (validationError) {
        setError(validationError);
        return;
      }

      const patch = buildPatch(form, initialForm, allowedFields);
      if (Object.keys(patch).length === 0) {
        setMessage("No changes to save.");
        return;
      }

      await patchUser(profile.id, patch);
      const reloaded = await getUser(profile.id);
      const reloadedForm = toForm(reloaded);
      setProfile(reloaded);
      setForm(reloadedForm);
      setInitialForm(reloadedForm);
      setMessage("Profile saved.");
      setAuditRefreshKey((key) => key + 1);
      onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="loading-row">Loading profile...</div>;
  }

  if (error && !profile) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!profile || !form || !initialForm) {
    return null;
  }

  return (
    <div className="profile-editor">
      <div className="profile-header">
        <div>
          <h2>{profile.displayName || profile.userPrincipalName}</h2>
          <p>{profile.userPrincipalName}</p>
        </div>
        <div className="editor-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setForm(initialForm);
              setError(null);
              setMessage(null);
            }}
            disabled={!dirty || saving}
          >
            <RotateCcw size={16} aria-hidden="true" />
            Reset
          </button>
          <button type="button" className="primary-button" onClick={save} disabled={!dirty || saving}>
            <Save size={16} aria-hidden="true" />
            {saving ? "Saving" : "Save"}
          </button>
        </div>
      </div>

      {message ? <div className="alert alert-success">{message}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="form-grid">
        {textFields.map((field) => (
          <label key={field.key} className="field-block">
            <span>{field.label}</span>
            <input
              value={form[field.key]}
              required={field.required}
              disabled={!allowedFields.has(field.key)}
              onChange={(event) =>
                setForm((current) => (current ? { ...current, [field.key]: event.target.value } : current))
              }
            />
          </label>
        ))}

        <label className="field-block field-block-wide">
          <span>Business phones</span>
          <textarea
            value={form.businessPhones}
            disabled={!allowedFields.has("businessPhones")}
            rows={3}
            onChange={(event) =>
              setForm((current) => (current ? { ...current, businessPhones: event.target.value } : current))
            }
          />
        </label>
      </div>

      <ManagerPicker
        division={division}
        value={form.manager}
        disabled={!allowedFields.has("manager")}
        onChange={(manager) => setForm((current) => (current ? { ...current, manager } : current))}
      />

      <PhotoUploader
        userId={profile.id}
        disabled={!allowedFields.has("photo")}
        onUploaded={() => setAuditRefreshKey((key) => key + 1)}
      />

      <AuditPanel targetUserId={profile.id} refreshKey={auditRefreshKey} />
    </div>
  );
}

function toForm(profile: EditableUserProfile): ProfileForm {
  return {
    displayName: profile.displayName || "",
    givenName: profile.givenName || "",
    surname: profile.surname || "",
    jobTitle: profile.jobTitle || "",
    department: profile.department || "",
    officeLocation: profile.officeLocation || "",
    mobilePhone: profile.mobilePhone || "",
    businessPhones: (profile.businessPhones || []).join("\n"),
    companyName: profile.companyName || "",
    employeeId: profile.employeeId || "",
    manager: profile.manager
  };
}

function validateForm(form: ProfileForm, division: string): string | null {
  if (!form.displayName.trim()) {
    return "Display name is required.";
  }

  if (form.department.trim().toLocaleLowerCase() !== division.trim().toLocaleLowerCase()) {
    return "Department must stay within your assigned division.";
  }

  return null;
}

function buildPatch(form: ProfileForm, initialForm: ProfileForm, allowedFields: Set<EditableField>): ProfilePatch {
  const patch: ProfilePatch = {};

  for (const field of textFields) {
    if (!allowedFields.has(field.key)) {
      continue;
    }

    if (form[field.key] !== initialForm[field.key]) {
      patch[field.key] = normalizeString(form[field.key]);
    }
  }

  if (allowedFields.has("businessPhones") && form.businessPhones !== initialForm.businessPhones) {
    patch.businessPhones = form.businessPhones
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (allowedFields.has("manager") && (form.manager?.id || null) !== (initialForm.manager?.id || null)) {
    patch.manager = form.manager ? { id: form.manager.id } : null;
  }

  return patch;
}

function normalizeString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
